import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'react-bootstrap';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Article } from '@/modules/articles/model/article';
import { openPopup } from '@/shared/util/export';
import { AnalyticsEvent, analyticsService, AnalyticsTypes } from '@/shared/analytics/export';
import { authService, useSessionId } from '@/core/auth/export';
import { articleApi } from '@/modules/articles/api';
import { DateFormat } from '@/core/date/export';
import ArticleBodyDisplay from '@/modules/articles/components/articles/article-body-display';
import { Loading } from '@/shared/components/export';
import BookmarkButton from '@/modules/articles/components/bookmarks/bookmark-button';
import styles from '@/modules/articles/components/articles/article-list.module.scss';
import embedLocationInXhtml from '@/modules/articles/util/embedLocationInXhtml';

function ArticleDisplay({ article }: { article: Article }) {
  const xhtml = embedLocationInXhtml(article);

  return (
    <div className={'h-100 h-100 overflow-auto d-flex flex-column container-element '} style={{}}>
      <div className={''}>
        <div className={'d-flex justify-content-between'}>
          <div className={'small '}>
            <div>
              <DateFormat format={'MMM D, h:mm a'} date={article.firstcreated} />,{' '}
              <strong>{dayjs(article.firstcreated).fromNow()}</strong>
            </div>
          </div>
          <div>
            {article.byline && <div className={'small fst-italic'}>By {article.byline}</div>}
          </div>
        </div>
      </div>

      <div className={`h-100 overflow-auto container-responsive-text mt-3 ${styles.articleModal}`}>
        <ArticleBodyDisplay article={article} xhtml={xhtml} />
      </div>
    </div>
  );
}

export default function ArticleModal({
  articleId,
  onClose,
}: {
  articleId?: string;
  onClose: () => void;
}) {
  const [article, setArticle] = useState<Article>();
  const [loading, setLoading] = useState<boolean>(false);

  const sessionId = useSessionId();
  const subject = authService.getSubjectData();

  useEffect(() => {
    setLoading(true);
    if (articleId) {
      articleApi
        .getArticle(articleId)
        .then(setArticle)
        .finally(() => setLoading(false));
    } else {
      setArticle(undefined);
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    if (article) {
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.ARTICLE_MODAL, {
        subject,
        object: {
          type: AnalyticsTypes.ARTICLE,
          id: article.uri,
          title: article.headline,
          genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
          subjects: article.subject.map(s => ({ id: `${s.scheme}:${s.code}`, name: s.name })),
          authors: article.byline,
        },
        session: {
          id: sessionId,
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const popOut = () => {
    if (article) {
      openPopup(`/article/${article.uri}`);
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.ARTICLE_POPOUT, {
        subject,
        object: {
          type: AnalyticsTypes.ARTICLE,
          id: article.uri,
          title: article.headline,
          genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
          subjects: article.subject.map(s => ({ id: `${s.scheme}:${s.code}`, name: s.name })),
          authors: article.byline,
        },
        session: {
          id: sessionId,
        },
      });
    }
    onClose();
  };

  return (
    <Modal show={articleId != null} onHide={onClose} size={'lg'} centered>
      <ModalHeader
        className={'align-items-baseline sticky-top'}
        style={{ backgroundColor: 'var(--bs-body-bg' }}
      >
        <span>
          <h3>{article?.headline}</h3>
        </span>
        <div className={'flex-shrink-0'}>
          {article && <BookmarkButton article={article} />}
          <button className={'btn btn-link btn-sm'} onClick={popOut}>
            <FontAwesomeIcon icon={faUpRightFromSquare} />
          </button>
          <button className={'btn btn-link link-danger btn-sm'} onClick={onClose}>
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        {loading && <Loading type={'grid'} />}
        {article && <ArticleDisplay article={article} />}
      </ModalBody>
    </Modal>
  );
}
